import { createFileRoute, Link } from "@tanstack/react-router";
import { MapPin } from "lucide-react";
import { regions, regionsByGroup } from "@/data/regions";
import { services } from "@/data/services";
import { Section, SectionHeading } from "@/components/site/Section";
import { CtaBand } from "@/components/site/CtaBand";
import { JsonLd } from "@/components/site/JsonLd";
import { breadcrumbSchema, meta } from "@/lib/schema";
import { groupSeoText } from "@/lib/regionSeo";
import { overviewKeywords } from "@/lib/localKeywords";


const title = "Werkgebied verhuisbedrijf Zuid-Holland & Nederland | Vakkundig";
const description =
  "Vakkundig verhuist in Den Haag, Rotterdam, Delft, Zoetermeer, Leiden, Rijswijk, Westland en bijna 60 plaatsen in Zuid-Holland en Nederland. 4,9/5 uit 507 reviews.";

export const Route = createFileRoute("/werkgebied/")({
  head: () =>
    meta({
      title,
      description,
      path: "/werkgebied",
      image: "team",
      keywords: overviewKeywords(regions.map((r) => `verhuisbedrijf ${r.city}`)),
    }),
  component: WerkgebiedIndex,
});

function WerkgebiedIndex() {
  return (
    <>
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Werkgebied", path: "/werkgebied" },
        ])}
      />

      <Section tone="navy">
        <SectionHeading
          as="h1"
          eyebrow="Werkgebied"
          title="Verhuisbedrijf in Den Haag en heel Nederland"
          intro={`Onze standplaats is Den Haag, maar wij verhuizen in ${regions.length} plaatsen door heel Nederland. Per stad weten we hoe het werkt met parkeren, venstertijden, hoogbouw en smalle trappenhuizen — van Amsterdam en Utrecht tot Eindhoven, Groningen en Maastricht. Ook internationale verhuizingen verzorgen wij.`}
          inverted
        />
      </Section>

      <Section>
        <div className="space-y-5 rounded-xl border border-border bg-background p-4">
          {regionsByGroup.map(({ group, items }) => (
            <details key={group} open>
              <summary className="cursor-pointer text-left font-display font-bold text-secondary">
                {group}{" "}
                <span className="ml-1 text-xs font-semibold text-muted-foreground">({items.length})</span>
              </summary>
              {groupSeoText[group] ? (
                <p className="mb-4 mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
                  {groupSeoText[group]}
                </p>
              ) : null}
              <div className="mt-3 flex flex-wrap gap-2 pb-2">
                {items.map((region) => (
                  <Link
                    key={region.slug}
                    to="/werkgebied/$slug"
                    params={{ slug: region.slug }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-secondary transition hover:border-brand hover:text-brand"
                  >
                    <MapPin className="size-3.5 text-brand" aria-hidden />
                    {region.city}
                  </Link>
                ))}
              </div>
            </details>
          ))}
        </div>

      </Section>


      <Section tone="sand">
        <SectionHeading
          eyebrow="Diensten"
          title="Wat wij in ons hele werkgebied doen"
          intro="Elke dienst kun je in elke plaats van ons werkgebied boeken, los of gecombineerd."
        />
        <div className="mt-6 flex flex-wrap gap-3">
          {services.map((s) => (
            <Link
              key={s.slug}
              to="/diensten/$slug"
              params={{ slug: s.slug }}
              className="rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold text-secondary transition hover:border-brand hover:text-brand"
            >
              {s.title}
            </Link>
          ))}
        </div>
      </Section>

      <CtaBand />
    </>
  );
}
